
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'

// module components
import SurveysForm from '@/modules/SurveysModule/components/SurveysForm/index.vue'
import SurveyVariant from '@/modules/SurveysModule/components/SurveyVariant/index.vue'
import VariantForm from '@/modules/SurveysModule/components/VariantModal/index.vue'

// realisation
import { surveys } from '@/core/realisations/surveys.realisation'

// types
import { FullSurveyT, VariantPayloadT, VariantT } from '@/core/types/survey.types'

// composable
import { useRoute } from 'vue-router'
import useNotifications from '@/common/composable/useNotifications'
import useActions from '@/common/composable/useActions'
import useHelpers from '@/common/composable/useHelpers'

const ADDITIONAL_VARIANT = 'Другое'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        loader: Loader,
        'failed-fetch': FailedFetch,
        'surveys-form': SurveysForm,
        'survey-variant': SurveyVariant,
        'variant-form': VariantForm,
    },
    setup() {
        const notifications = useNotifications()
        const { findItemIndexById } = useHelpers()

        const route = useRoute()
        const survey_id = Number(route.params.survey_id)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            survey: null as FullSurveyT | null,
        })

        const loadSurvey = async () => {
            try {
                state.is_loading = true

                const response = await surveys.getSurvey(survey_id)
                state.survey = response.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true

                console.error(error)
                notifications.notifyError({ error, title: 'При получении опроса произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadSurvey)

        const onVariantDeleted = (variant_id: number) => {
            if (!state.survey) return

            const index = findItemIndexById(variant_id, state.survey.variants)
            if (index < 0) return

            state.survey.variants.splice(index, 1)
        }

        const { actions_state, startCreate, startUpdate } = useActions<VariantT>()

        const isHaveAdditionalVariant = computed(() => {
            if (!state.survey) return false
            if (!state.survey.variants.length) return false

            const finded = state.survey.variants.find(
                (variant) => variant.title.toUpperCase() === ADDITIONAL_VARIANT.toUpperCase()
            )

            if (finded) return true
            return false
        })

        const createVariant = async (variant: VariantPayloadT) => {
            if (
                variant.title.toUpperCase() === ADDITIONAL_VARIANT.toUpperCase() &&
                isHaveAdditionalVariant.value
            ) {
                notifications.notify({
                    title: `Вариант '${ADDITIONAL_VARIANT}' уже создан`,
                    type: 'danger',
                })

                return
            }

            try {
                actions_state.is_actions_loading = true

                const response = await surveys.createVariant(survey_id, variant)

                if (state.survey) {
                    state.survey.variants.unshift(response.data)
                }

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При добавлении варианта ответа произошла ошибка',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const addAdditionalVariant = () => {
            const answer = confirm(
                `Вы действительно хотите добавить вариант '${ADDITIONAL_VARIANT}'?`
            )
            if (!answer) return

            createVariant({ title: ADDITIONAL_VARIANT })
        }

        const updateVariant = async (variant: VariantPayloadT) => {
            if (!actions_state.initial_value) {
                return
            }

            try {
                actions_state.is_actions_loading = true

                const response = await surveys.updateVariant(
                    survey_id,
                    actions_state.initial_value.id,
                    variant
                )

                if (state.survey) {
                    const index = findItemIndexById(
                        actions_state.initial_value.id,
                        state.survey.variants
                    )

                    if (index < 0) return

                    state.survey.variants[index].title = response.data.title
                    state.survey.variants[index].answersCount = response.data.answersCount
                }

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При редактировании варианта ответа произошла ошибка',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const totalComputedAnswers = computed(() => {
            if (!state.survey) return 0
            return state.survey.variants.reduce((partialSum, a) => partialSum + a.answersCount, 0)
        })

        return {
            state,
            survey_id,
            loadSurvey,
            onVariantDeleted,
            actions_state,
            startCreate,
            startUpdate,
            createVariant,
            updateVariant,
            totalComputedAnswers,
            isHaveAdditionalVariant,
            addAdditionalVariant,
        }
    },
})
